import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, TextField,
    Button, Card, CardContent, Grid, Chip, InputAdornment, Tooltip,
    Tab, Tabs, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    Rule as RuleIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    TrendingUp as TrendingUpIcon,
    ExpandMore as ExpandMoreIcon,
    Inventory as InventoryIcon,
    Medication as MedicineIcon,
    Receipt as ReceiptIcon,
    MedicalServices as ProceduresIcon
} from '@mui/icons-material';
import axios from 'axios';
import flaskServer from '../config/flaskserver';

const RegrasAuditoria = () => {
    const [regras, setRegras] = useState({ precos: [], customizadas: [], associacoes: [] });
    const [filter, setFilter] = useState('');
    const [tab, setTab] = useState(0);
    const [editId, setEditId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [convenios, setConvenios] = useState([]);
    const [selectedConvenio, setSelectedConvenio] = useState('');
    const [catAssoc, setCatAssoc] = useState('TODOS');

    useEffect(() => {
        if (selectedConvenio) {
            carregarRegras();
        }
        carregarConvenios();
    }, [selectedConvenio]);

    const carregarRegras = async () => {
        if (!selectedConvenio) return;
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const url = `${flaskServer.flaskServerIP}/auditoria/regras?convenio_id=${selectedConvenio}`;
            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.status === 'error') {
                setError(response.data.message);
            } else {
                setRegras(response.data);
            }
        } catch (err) {
            console.error("Erro ao carregar regras:", err);
            setError(err.response?.data?.message || err.message || "Erro de conexão");
        } finally {
            setLoading(false);
        }
    };

    const carregarConvenios = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/convenios`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const convs = response.data.convenios || [];
            setConvenios(convs);
            if (convs.length > 0 && !selectedConvenio) {
                setSelectedConvenio(convs[0].id);
            }
        } catch (err) {
            console.error("Erro ao carregar convênios:", err);
        }
    };

    const handleEdit = (item) => {
        setEditId(`${item.codigo}-${item.convenio_id}`);
        setEditValue(item.media || item.valor);
    };

    const handleSave = async (item) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${flaskServer.flaskServerIP}/auditoria/regras/ajustar`, {
                codigo: item.codigo,
                convenio_id: item.convenio_id,
                novo_valor: editValue,
                tipo: 'preco_fixo'
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setEditId(null);
            carregarRegras();
        } catch (err) {
            alert('Erro ao salvar ajuste');
        }
    };

    const handleDeleteRule = async (tipo, id) => {
        if (!window.confirm('Deseja realmente excluir esta regra?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${flaskServer.flaskServerIP}/auditoria/regras/${tipo}/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            carregarRegras();
        } catch (err) {
            alert('Erro ao excluir regra');
        }
    };

    const [rebuilding, setRebuilding] = useState(false);
    const [rebuildMsg, setRebuildMsg] = useState('');

    const rebuildCache = async () => {
        setRebuilding(true);
        setRebuildMsg('Calculando associações... isso pode levar alguns segundos.');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${flaskServer.flaskServerIP}/auditoria/regras/rebuild-cache`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setRebuildMsg(response.data.message || 'Cache recalculado!');
            carregarRegras();
        } catch (err) {
            setRebuildMsg('Erro ao recalcular: ' + (err.response?.data?.message || err.message));
        } finally {
            setRebuilding(false);
        }
    };

    const handleClearCategory = async (categoria) => {
        if (!window.confirm(`Deseja realmente LIMPAR TODAS as regras de ${categoria}?`)) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${flaskServer.flaskServerIP}/auditoria/regras/limpar`, {
                categoria: categoria,
                convenio_id: 'null' // Pode ser expandido para filtro por convênio no futuro
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            carregarRegras();
        } catch (err) {
            alert('Erro ao limpar categoria');
        }
    };

    const filteredPrecos = (regras.precos || []).filter(p =>
        (p.codigo || '').includes(filter) ||
        (p.descricao || '').toLowerCase().includes(filter.toLowerCase()) ||
        (p.convenio || '').toLowerCase().includes(filter.toLowerCase())
    );

    const filteredCustoms = (regras.customizadas || []).filter(c =>
        (c.codigo || '').includes(filter)
    );

    const filteredAssoc = (regras.associacoes || []).filter(a => {
        const matchesText = (a.procedimento_codigo || '').includes(filter) ||
            (a.procedimento_desc || '').toLowerCase().includes(filter.toLowerCase()) ||
            (a.item_desc || '').toLowerCase().includes(filter.toLowerCase());

        if (!matchesText) return false;

        if (catAssoc === 'TODOS') return true;
        if (catAssoc === 'TAXA') return ['Taxa', 'Taxas', 'Diária', 'Diárias'].includes(a.item_tipo);
        if (catAssoc === 'MAT_MED') return ['Material', 'Medicamento', 'Insumo'].includes(a.item_tipo);
        if (catAssoc === 'PROCEDIMENTO') return a.item_tipo === 'Procedimento';
        return true;
    });

    // Agrupar associações por procedimento principal
    const [showAllAssoc, setShowAllAssoc] = useState(false);

    const groupedAssoc = filteredAssoc.reduce((acc, current) => {
        const key = current.procedimento_codigo;
        if (!acc[key]) {
            acc[key] = {
                codigo: current.procedimento_codigo,
                descricao: current.procedimento_desc,
                itens: []
            };
        }
        acc[key].itens.push(current);
        return acc;
    }, {});

    const fullGroupedList = Object.values(groupedAssoc).sort((a, b) => b.itens.length - a.itens.length);
    const groupedAssocList = showAllAssoc ? fullGroupedList : fullGroupedList.slice(0, 50);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
                <RuleIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Box>
                    <Typography variant="h4" fontWeight="bold" color="secondary">
                        Gestão de Regras de Auditoria
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Configure preços de referência e exceções por convênio
                    </Typography>
                </Box>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">Convênio:</Typography>
                    <TextField
                        select
                        size="small"
                        value={selectedConvenio}
                        onChange={(e) => setSelectedConvenio(e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ minWidth: 200 }}
                    >
                        {convenios.map((c) => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                    </TextField>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={rebuildCache}
                        disabled={rebuilding}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {rebuilding ? '⏳ Calculando...' : '🔄 Recalcular Pacotes'}
                    </Button>
                </Box>
            </Box>

            {rebuildMsg && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f0fdf4', border: '1px solid #22c55e', borderRadius: 2 }}>
                    <Typography color="success.main" fontWeight="bold">{rebuildMsg}</Typography>
                </Paper>
            )}

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                        <CardContent>
                            <Typography variant="subtitle2">PADRÕES APRENDIDOS</Typography>
                            <Typography variant="h4" fontWeight="bold">{regras.precos?.length || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
                        <CardContent>
                            <Typography variant="subtitle2">AJUSTES MANUAIS ATIVOS</Typography>
                            <Typography variant="h4" fontWeight="bold">{regras.customizadas?.length || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {error && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#fee2e2', border: '1px solid #ef4444', borderRadius: 2 }}>
                    <Typography color="error" fontWeight="bold">Erro ao carregar dados:</Typography>
                    <Typography color="error">{error}</Typography>
                    <Button variant="outlined" color="error" size="small" sx={{ mt: 1 }} onClick={carregarRegras}>
                        Tentar Novamente
                    </Button>
                </Paper>
            )}

            {loading && !regras.precos?.length && (
                <Box display="flex" justifyContent="center" p={5}>
                    <Typography color="textSecondary">Carregando regras...</Typography>
                </Box>
            )}

            <Paper className="premium-panel" sx={{ p: 3, borderRadius: 3, opacity: loading ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                        <Tab label="Preços Médios (Aprendidos)" />
                        <Tab label="Ajustes e Exceções (Custom)" />
                        <Tab label="Associações e Pacotes" />
                    </Tabs>
                    <TextField
                        size="small"
                        placeholder="Filtrar..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        sx={{ width: 300 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleClearCategory(tab === 0 ? 'preco' : tab === 1 ? 'custom' : 'associacao')}
                    >
                        Limpar Aba
                    </Button>
                </Box>

                {tab === 0 && (
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <TableRow>
                                    <TableCell><strong>Código</strong></TableCell>
                                    <TableCell><strong>Descrição</strong></TableCell>
                                    <TableCell><strong>Convênio</strong></TableCell>
                                    <TableCell align="right"><strong>Média</strong></TableCell>
                                    <TableCell align="center"><strong>Status</strong></TableCell>
                                    <TableCell align="center"><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredPrecos.map((item) => {
                                    const isEditing = editId === `${item.codigo}-${item.convenio_id}`;
                                    const hasCustom = (regras.customizadas || []).some(c => c.codigo === item.codigo && c.convenio_id === item.convenio_id);

                                    return (
                                        <TableRow key={`${item.codigo}-${item.convenio_id}`} hover>
                                            <TableCell>{item.codigo}</TableCell>
                                            <TableCell sx={{ maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.descricao}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={item.convenio} size="small" color="primary" variant="outlined" />
                                            </TableCell>
                                            <TableCell align="right">
                                                {isEditing ? (
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        sx={{ width: 100 }}
                                                    />
                                                ) : (
                                                    `R$ ${(item.media || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {hasCustom ? <Chip label="Ajustado" size="small" color="success" /> : <Chip label="Sugerido" size="small" variant="outlined" />}
                                            </TableCell>
                                            <TableCell align="center">
                                                {isEditing ? (
                                                    <IconButton color="success" onClick={() => handleSave(item)}>
                                                        <SaveIcon />
                                                    </IconButton>
                                                ) : (
                                                    <Box>
                                                        <IconButton color="primary" onClick={() => handleEdit(item)} size="small">
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton color="error" onClick={() => handleDeleteRule('preco', item.codigo)} size="small">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {tab === 1 && (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Código</strong></TableCell>
                                    <TableCell><strong>Convênio</strong></TableCell>
                                    <TableCell><strong>Tipo de Regra</strong></TableCell>
                                    <TableCell><strong>Valor Ref.</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCustoms.map((c) => (
                                    <TableRow key={c.id}>
                                        <TableCell>{c.codigo}</TableCell>
                                        <TableCell>
                                            <Chip label={c.convenio} size="small" variant="outlined" color="primary" />
                                        </TableCell>
                                        <TableCell>
                                            {c.tipo === 'preco_fixo' ? <Chip icon={<TrendingUpIcon />} label="Preço Fixo" size="small" /> : <Chip icon={<BlockIcon />} label="Ignorar" size="small" color="error" />}
                                        </TableCell>
                                        <TableCell align="right">
                                            {c.valor ? `R$ ${c.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={c.ativo ? 'Ativa' : 'Inativa'} color={c.ativo ? 'success' : 'default'} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteRule('custom', c.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {tab === 2 && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                            <Button
                                variant={catAssoc === 'TODOS' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setCatAssoc('TODOS')}
                                startIcon={<SearchIcon />}
                            >
                                Todos
                            </Button>
                            <Button
                                variant={catAssoc === 'PROCEDIMENTO' ? 'contained' : 'outlined'}
                                size="small"
                                color="secondary"
                                onClick={() => setCatAssoc('PROCEDIMENTO')}
                                startIcon={<ProceduresIcon />}
                            >
                                Procedimentos
                            </Button>
                            <Button
                                variant={catAssoc === 'TAXA' ? 'contained' : 'outlined'}
                                size="small"
                                color="info"
                                onClick={() => setCatAssoc('TAXA')}
                                startIcon={<ReceiptIcon />}
                            >
                                Taxas/Diárias
                            </Button>
                            <Button
                                variant={catAssoc === 'MAT_MED' ? 'contained' : 'outlined'}
                                size="small"
                                color="success"
                                onClick={() => setCatAssoc('MAT_MED')}
                                startIcon={<MedicineIcon />}
                            >
                                Mat/Med
                            </Button>
                        </Box>

                        {groupedAssocList.map((grupo) => (
                            <Accordion key={grupo.codigo} sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box display="flex" alignItems="center" width="100%">
                                        <Box sx={{ bgcolor: 'secondary.light', p: 1, borderRadius: 1, mr: 2, display: 'flex' }}>
                                            <InventoryIcon sx={{ color: 'secondary.main' }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                                                {grupo.codigo} - {grupo.descricao}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {grupo.itens.length} itens detectados no pacote
                                            </Typography>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                                <TableRow>
                                                    <TableCell><strong>Item do Pacote</strong></TableCell>
                                                    <TableCell><strong>Tipo</strong></TableCell>
                                                    <TableCell align="center"><strong>Frequência</strong></TableCell>
                                                    <TableCell align="center"><strong>Ações</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {grupo.itens.map((item, idx) => (
                                                    <TableRow key={idx} hover>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="bold">{item.item_codigo}</Typography>
                                                            <Typography variant="caption" color="textSecondary">{item.item_desc}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={item.item_tipo} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Typography variant="body2" fontWeight="bold" color={item.frequencia > 80 ? 'success.main' : 'primary'}>
                                                                {item.frequencia}%
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Em breve: Bloquear item para este procedimento">
                                                                <IconButton size="small" disabled>
                                                                    <BlockIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {fullGroupedList.length > 50 && !showAllAssoc && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Button variant="outlined" onClick={() => setShowAllAssoc(true)}>
                                    Ver todos os {fullGroupedList.length} pacotes detectados
                                </Button>
                            </Box>
                        )}

                        {groupedAssocList.length === 0 && (
                            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.01)', border: '1px dashed #ccc' }}>
                                <Typography color="textSecondary">
                                    Nenhuma associação aprendida ainda. Importe guias completas para gerar o modelo estatístico.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default RegrasAuditoria;
